import type { VariableDef } from './Variables';

/**
 * Ready-made variables.
 *
 * Most terminal dashboards show the same handful of things, and each one has a
 * type, a sensible width and a formatter that people otherwise have to work out
 * for themselves. Picking from this list is the difference between "what is a
 * variable?" and "show the username here".
 */

export interface VariablePreset {
  def: VariableDef;
  /** What this is for, in the user's terms. */
  blurb: string;
  group: string;
}

export const VARIABLE_PRESETS: readonly VariablePreset[] = [
  {
    group: 'People',
    blurb: 'Who is logged in',
    def: {
      name: 'username',
      type: 'string',
      description: 'The name of the current user',
      defaultValue: 'guest',
      previewValue: 'alice',
      fallback: 'guest',
      width: 12,
      align: 'left',
    },
  },
  {
    group: 'People',
    blurb: 'How many people are connected',
    def: {
      name: 'connected_users',
      type: 'integer',
      description: 'Users currently online',
      defaultValue: 0,
      previewValue: 438,
      fallback: 0,
      width: 4,
      align: 'right',
    },
  },
  {
    group: 'People',
    blurb: 'Unread or total messages',
    def: {
      name: 'message_count',
      type: 'integer',
      defaultValue: 0,
      previewValue: 1893,
      width: 5,
      align: 'right',
      format: 'abbrev',
    },
  },
  {
    group: 'System',
    blurb: 'Processor load as a percentage',
    def: {
      name: 'cpu',
      type: 'percentage',
      defaultValue: 0,
      previewValue: 61,
      width: 4,
      align: 'right',
      colorRules: [
        { max: 60, color: '#4ade80' },
        { min: 61, max: 85, color: '#fbbf24' },
        { min: 86, color: '#f87171' },
      ],
    },
  },
  {
    group: 'System',
    blurb: 'Memory use as a percentage',
    def: {
      name: 'ram',
      type: 'percentage',
      defaultValue: 0,
      previewValue: 82,
      width: 4,
      align: 'right',
      colorRules: [
        { max: 70, color: '#4ade80' },
        { min: 71, max: 90, color: '#fbbf24' },
        { min: 91, color: '#f87171' },
      ],
    },
  },
  {
    group: 'System',
    blurb: 'How long the machine has been up',
    def: {
      name: 'uptime',
      type: 'duration',
      defaultValue: 0,
      previewValue: 394234,
      width: 9,
      align: 'left',
    },
  },
  {
    group: 'System',
    blurb: 'A progress bar drawn from a percentage',
    def: {
      name: 'progress',
      type: 'percentage',
      defaultValue: 0,
      previewValue: 64,
      format: 'bar:12',
      width: 12,
    },
  },
  {
    group: 'Network',
    blurb: 'Round-trip latency',
    def: {
      name: 'ping',
      type: 'integer',
      defaultValue: 0,
      previewValue: 21,
      width: 6,
      align: 'right',
      format: 'suffix: ms',
      colorRules: [
        { max: 50, color: '#4ade80' },
        { min: 51, max: 150, color: '#fbbf24' },
        { min: 151, color: '#f87171' },
      ],
    },
  },
  {
    group: 'Network',
    blurb: 'Which server this is',
    def: {
      name: 'server_name',
      type: 'string',
      defaultValue: 'local',
      previewValue: 'EU-01',
      width: 10,
    },
  },
  {
    group: 'Network',
    blurb: 'Host or domain name',
    def: {
      name: 'hostname',
      type: 'string',
      defaultValue: 'localhost',
      previewValue: 'node-eu-01.internal',
      width: 20,
    },
  },
  {
    group: 'Status',
    blurb: 'A short status word',
    def: {
      name: 'status',
      type: 'string',
      defaultValue: 'UNKNOWN',
      previewValue: 'HEALTHY',
      width: 8,
      colorRules: [
        { equals: 'HEALTHY', color: '#4ade80' },
        { equals: 'DEGRADED', color: '#fbbf24' },
        { equals: 'DOWN', color: '#f87171' },
      ],
    },
  },
  {
    group: 'Status',
    blurb: 'Current time of day',
    def: {
      name: 'clock',
      type: 'timestamp',
      previewValue: Date.now(),
      format: 'time:HH:mm:ss',
      width: 8,
    },
  },
  {
    group: 'Status',
    blurb: "Today's date",
    def: {
      name: 'date',
      type: 'timestamp',
      previewValue: Date.now(),
      format: 'date:YYYY-MM-DD',
      width: 10,
    },
  },
  {
    group: 'Status',
    blurb: 'Version string',
    def: {
      name: 'version',
      type: 'string',
      defaultValue: '1.0.0',
      previewValue: '1.4.2',
      width: 8,
    },
  },
];

export function presetGroups(): string[] {
  return [...new Set(VARIABLE_PRESETS.map((p) => p.group))];
}
